import React, { SyntheticEvent } from 'react'
import { ApolloClient } from 'apollo-client'
import { NormalizedCache } from 'apollo-cache-inmemory'
import gql from 'graphql-tag'
import { Dispatch } from 'redux'
import { connect } from 'react-redux'

import { LoginContainer } from './Common'
import CloseButton from '../Shared/CloseButton'
import { hideSignUpForm, signUpNextForm } from '../../actions/ui_actions'
import { Store } from '../../reducers'
import { Action } from '../../actions/types'
import { recordEvent } from '../../analytics'
import { compose } from 'react-apollo'
import { CountryType, withCountries } from '../Shared/CountryOptions'
import styled from '../../theme/styled'

type SignUpFormContext = {
  client: ApolloClient<NormalizedCache>
}

type Props = {
  close: () => Action
  ready: (email: string) => Action
  countries: CountryType[]
}

type SignUpFormData = {
  email: string
  firstName: string
  lastName: string
  company: string
  country: string
  profession: string
  sendInfo: boolean
  acceptance: boolean
}

type State = {
  formData: SignUpFormData
  error: string | null
  busy: boolean
}

const ArrowImg = require('../Shared/arrow_down.svg')

const SelectWrapper = styled.div`
  margin-left: auto;

  .custom-select {
    background-image: url(${ArrowImg});
    background-position: calc(100% - 12px);
    background-size: 8px 8px;
    background-repeat: no-repeat;
    border-radius: 0px;
    width: 100%;
  }
`

const stateDefaults: State = {
  formData: {
    email: '',
    firstName: '',
    lastName: '',
    company: '',
    country: 'FI',
    profession: 'Structural Designer',
    sendInfo: false,
    acceptance: false
  },
  error: null,
  busy: false
}

class SignUpForm extends React.Component<Props, State> {
  static contextTypes = {
    client: () => null
  }

  context: SignUpFormContext

  constructor(props: Props) {
    super(props)
    this.state = stateDefaults
  }

  componentWillMount() {
    recordEvent('user', 'registration-form-opened')
    this.setState(stateDefaults) // TODO: why is this here? We set these in constructor
  }

  checkFormData = (formData: SignUpFormData) => {
    return (
      formData.acceptance &&
      formData.company !== '' &&
      formData.country !== '' &&
      formData.email !== '' &&
      formData.firstName !== '' &&
      formData.lastName !== '' &&
      formData.profession !== ''
    )
  }

  textChanged = event => {
    const formData = {
      ...this.state.formData,
      [event.currentTarget.name]: event.currentTarget.value
    }
    this.setState({ formData })
  }

  checkChanged = event => {
    const formData = {
      ...this.state.formData,
      [event.currentTarget.name]: event.currentTarget.checked
    }
    this.setState({ formData })
  }

  signUp = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault()
    const { acceptance, ...signUpData } = this.state.formData
    this.setState({ busy: true }, () => {
      this.context.client
        .mutate<Response>({
          mutation: gql`
            mutation RegisterUser($registerInput: PdRegisterInput!) {
              registerUser(registerInput: $registerInput) {
                email
                errorMsg
                errorCode
              }
            }
          `,
          variables: { registerInput: signUpData }
        })
        .then(({ data }) => {
          if (!data) {
            throw new Error('No data')
          }
          const { errorCode, errorMsg, email } = data.registerUser
          if (errorCode !== 0) {
            this.setState({ busy: false, error: errorMsg })
          } else {
            this.setState({ busy: false, error: null }, () => {
              recordEvent('user', 'registration-complete')
              this.props.ready(email)
            })
          }
        })
        .catch(err => {
          console.warn('Signup failed', err)
          this.setState({ busy: false, error: 'Signup failed' })
        })
    })
  }

  render() {
    const { close, countries = [] } = this.props
    const { busy, formData } = this.state

    const ok = this.checkFormData(formData)

    return (
      <LoginContainer>
        <div className="modal-inner">
          <h4>
            Sign up
            <div className="spacer" />
            <CloseButton
              white={true}
              style={{ float: 'right' }}
              onClick={close}
            />
          </h4>
          <div className="content">
            <form onSubmit={this.signUp}>
              {this.state.error && <p>{this.state.error}</p>}
              <input
                name="firstName"
                onChange={this.textChanged}
                type="text"
                placeholder="First name"
                required={true}
              />
              <input
                name="lastName"
                onChange={this.textChanged}
                type="text"
                placeholder="Last name"
                required={true}
              />
              <input
                name="email"
                onChange={this.textChanged}
                type="email"
                placeholder="Email address (username)"
                required={true}
              />
              <input
                name="company"
                onChange={this.textChanged}
                type="text"
                placeholder="Company"
                required={true}
              />

              <div className="split">
                <SelectWrapper>
                  <select
                    name="profession"
                    onChange={this.textChanged}
                    className="custom-select"
                  >
                    <option value="Structural Designer">
                      Structural Designer
                    </option>
                    <option value="Architect">Architect</option>
                    <option value="Student">Student</option>
                    <option value="Project Engineer">Project Engineer</option>
                    <option value="Project Manager">Project Manager</option>
                    <option value="Site Engineer">Site Engineer</option>
                    <option value="Site Manager">Site Manager</option>
                    <option value="Other">Other</option>
                  </select>
                </SelectWrapper>

                <SelectWrapper>
                  <select
                    name="country"
                    className="custom-select"
                    onChange={this.textChanged}
                    value={formData.country}
                  >
                    {countries.map(country => (
                      <option key={country.id} value={country.id}>
                        {country.displayName}
                      </option>
                    ))}
                  </select>
                </SelectWrapper>
              </div>

              <div className="checkbox-row">
                <input
                  type="checkbox"
                  onChange={this.checkChanged}
                  checked={this.state.formData.acceptance}
                  name="acceptance"
                  id="terms-and-privacy"
                />
                <label htmlFor="terms-and-privacy">
                  I accept the Agreement: Terms of Use and Privacy policy
                </label>
              </div>

              <div className="checkbox-row">
                <input
                  type="checkbox"
                  onChange={this.checkChanged}
                  checked={this.state.formData.sendInfo}
                  name="sendInfo"
                  id="subscribe-to-newsletter"
                />
                <label htmlFor="subscribe-to-newsletter">
                  Yes, please send me information about new features and tools,
                  as well as software and Peikko's product updates.
                </label>
              </div>

              <input
                type="submit"
                name="submit"
                value={busy ? 'Working...' : 'Sign up'}
                disabled={!ok || busy}
              />
            </form>
          </div>
        </div>
        {busy && <div className="spinner" />}
      </LoginContainer>
    )
  }
}

const mapStateToProps = (state: Store.Complete) => ({})

const mapDispatchToProps = (dispatch: Dispatch<Store.Complete>) => ({
  close: () => dispatch(hideSignUpForm()),
  ready: email => dispatch(signUpNextForm(email))
})

export default compose(
  withCountries,
  connect(mapStateToProps, mapDispatchToProps)
)(SignUpForm)



// WEBPACK FOOTER //
// ./src/Components/LoginForm/SignUp.tsx